import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useSiteImages } from "@/hooks/useSiteImages";
import { Marquee } from "@/components/Marquee";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

const MONTREAL_1 = "https://images.unsplash.com/photo-1616034328430-a1ec65cabb01?crop=entropy&cs=srgb&fm=jpg&q=85";
const MONTREAL_2 = "https://images.unsplash.com/photo-1723921027368-c19d7f757fbd?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function About() {
  const { t } = useLanguage();
  const img = useSiteImages();
  const ABOUT_1 = img("about_1", MONTREAL_1);
  const ABOUT_2 = img("about_2", MONTREAL_2);
  const FOUNDER_IMG = img("founder", "");
  useSEO(t("nav.about") + " — Diamotims Agency", t("about.intro"));

  const PILLARS = [
    { n: "01", title: t("about.p1Title"), body: t("about.p1Body") },
    { n: "02", title: t("about.p2Title"), body: t("about.p2Body") },
    { n: "03", title: t("about.p3Title"), body: t("about.p3Body") },
  ];
  const VALUES = [t("about.v1"), t("about.v2"), t("about.v3"), t("about.v4")];
  const BIO = [t("about.founderBio1"), t("about.founderBio2"), t("about.founderBio3"), t("about.founderBio4"), t("about.founderBio5"), t("about.founderBio6")];
  const nameParts = t("about.founderName").split(" ");
  const founderLast = nameParts.pop();
  const founderFirst = nameParts.join(" ");

  return (
    <main data-testid="about-page" className="pt-28 lg:pt-36">
      <div className="mx-auto max-w-[1800px] px-4 sm:px-8 lg:px-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("about.eyebrow")}</p>
        <h1 className="mt-4 font-display text-5xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.1}>{t("about.title1")}</MaskedLine>
          <MaskedLine delay={0.22}>
            <span className="italic text-gold">{t("about.title2")}</span>
          </MaskedLine>
        </h1>
        <FadeUp delay={0.35}>
          <p className="mt-8 max-w-2xl font-display text-xl font-light italic leading-relaxed text-white/75 sm:text-2xl">
            {t("about.intro")}
          </p>
        </FadeUp>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-10">
          <FadeUp className="md:col-span-7">
            <div className="h-full overflow-hidden border border-white/10">
              {ABOUT_1 ? (
                <img src={ABOUT_1} alt="Montréal" loading="lazy" className="h-full min-h-[320px] w-full object-cover grayscale-[30%] transition-[transform,filter] duration-700 hover:scale-[1.03] hover:grayscale-0" />
              ) : (
                <div className="h-full min-h-[320px] w-full animate-pulse bg-white/5" />
              )}
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-5">
            <div className="flex h-full flex-col justify-between gap-8 border border-white/10 bg-[#121212] p-8 lg:p-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("about.missionLabel")}</div>
              <p className="font-display text-2xl font-light italic leading-snug text-white/85 lg:text-3xl">
                {t("about.quote")}
              </p>
              <p className="text-sm leading-relaxed text-white/60">{t("about.missionBody")}</p>
            </div>
          </FadeUp>
          <FadeUp className="md:col-span-5">
            <div className="h-full overflow-hidden border border-white/10">
              {ABOUT_2 ? (
                <img src={ABOUT_2} alt="Montréal" loading="lazy" className="h-full min-h-[320px] w-full object-cover grayscale-[30%] transition-[transform,filter] duration-700 hover:scale-[1.03] hover:grayscale-0" />
              ) : (
                <div className="h-full min-h-[320px] w-full animate-pulse bg-white/5" />
              )}
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-7">
            <div className="grid h-full grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
              {PILLARS.map((p) => (
                <div key={p.n} className="bg-[#0A0A0A] p-8">
                  <div className="font-mono text-xs text-gold">{p.n}</div>
                  <h3 className="mt-4 font-display text-2xl font-light uppercase tracking-tight text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{p.body}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mt-20">
          <div className="border-y border-white/10 py-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t("about.commitments")}</div>
            <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              {VALUES.map((v) => (
                <li key={v} className="flex items-start gap-3 text-sm leading-relaxed text-white/70">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                  {v}
                </li>
              ))}
            </ul>
          </div>
        </FadeUp>

        <section className="relative mt-24 overflow-hidden border-t border-white/10 pt-16 lg:mt-32 lg:pt-24">
          <div className="pointer-events-none absolute -top-4 right-0 select-none" aria-hidden="true">
            <span
              className="whitespace-nowrap font-display text-[18vw] font-light uppercase leading-none text-transparent lg:text-[11vw]"
              style={{ WebkitTextStroke: "1px rgba(250,248,245,0.07)" }}
            >
              Founder
            </span>
          </div>

          <div className="relative mt-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
            {FOUNDER_IMG && (
              <FadeUp delay={0.3} className="lg:col-span-4">
                <div className="relative mx-auto max-w-sm lg:mx-0">
                  <div className="absolute -left-3 -top-3 h-full w-full border border-gold/40" aria-hidden="true" />
                  <img src={FOUNDER_IMG} alt={t("about.founderName")} className="relative aspect-[3/4] w-full object-cover object-top grayscale-[30%]" />
                </div>
              </FadeUp>
            )}
            <div className={FOUNDER_IMG ? "lg:col-span-8" : "lg:col-span-12"}>
              <FadeUp>
                <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("about.leadership")}</div>
              </FadeUp>
              <h3 className="relative mt-6 font-display text-5xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
                <MaskedLine delay={0.1}>{founderFirst}</MaskedLine>
                <MaskedLine delay={0.22}>
                  <span className="italic text-gold">{founderLast}</span>
                </MaskedLine>
              </h3>
              <FadeUp delay={0.3}>
                <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                  {t("about.founderRole").split("|").map((part, i, arr) => (
                    <span key={i} className="flex items-center gap-4">
                      {part.trim()}
                      {i < arr.length - 1 && <span className="h-1 w-1 rotate-45 bg-gold/60" aria-hidden="true" />}
                    </span>
                  ))}
                </div>
              </FadeUp>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12">
            <FadeUp delay={0.35} className="lg:col-span-5">
              <p className="font-display text-xl font-light italic leading-relaxed text-white/85 sm:text-2xl">
                {BIO[0]}
              </p>
            </FadeUp>
            <div className="grid gap-10 sm:grid-cols-2 lg:col-span-7">
              {BIO.slice(1, 5).map((p, i) => (
                <FadeUp key={i} delay={0.4 + i * 0.08}>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-gold/50">{String(i + 2).padStart(2, "0")}</div>
                  <p className="mt-3 border-t border-white/10 pt-4 text-sm leading-relaxed text-white/60">{p}</p>
                </FadeUp>
              ))}
            </div>
          </div>

          <FadeUp delay={0.2} className="mt-16 lg:mt-20">
            <div className="border-l-2 border-gold pl-6 sm:pl-10">
              <p className="max-w-4xl font-display text-2xl font-light italic leading-snug text-ivory sm:text-3xl lg:text-4xl">
                {BIO[5]}
              </p>
            </div>
          </FadeUp>
        </section>

        <FadeUp className="py-20 text-center lg:py-28">
          <p className="font-display text-2xl font-light italic text-white/75 sm:text-3xl">
            {t("about.cta")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/clients" data-testid="about-clients-link" className="group flex items-center gap-3 bg-ivory px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold">
              {t("about.workWithUs")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <Link to="/submissions" data-testid="about-submissions-link" className="flex items-center gap-3 border border-white/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:border-gold hover:text-gold">
              {t("about.apply")}
            </Link>
          </div>
        </FadeUp>
      </div>

      <Marquee items={["Fashion", "Film", "Television", "Commercial", "Voice-Over", "Animation", "Audiobooks", "Theatre", "Video Games"]} />
    </main>
  );
}
